import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { FiMusic, FiPause, FiPlay, FiSkipForward, FiX } from "react-icons/fi";
import { useRadio } from "./RadioContext";

/*
 * Plutajući radio widget (dole levo): skupljen je okrugla dugmad sa
 * ekvilajzerom kad svira; raširen je mini player sa imenom pesme,
 * play/pauza i "sledeća". Prvi put pita korisnika da li želi muziku.
 */

const RadioWidget = () => {
  const radio = useRadio();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);

  // Auto-raširi kad krene muzika
  useEffect(() => {
    if (radio?.playing) setExpanded(true);
  }, [radio?.playing]);

  if (!radio || !radio.available) return null;

  // Ne prikazuj na admin/bend panelu i login stranici
  if (
    pathname.startsWith("/admin-panel") ||
    pathname.startsWith("/bend-panel") ||
    pathname.startsWith("/login")
  ) {
    return null;
  }

  // Na product stranici mobilna CTA traka zauzima dno, podigni widget
  const raised = pathname.startsWith("/proizvod/");
  const showPrompt = radio.shouldPrompt && !promptDismissed;

  return (
    <Wrap $raised={raised}>
      {showPrompt && (
        <PromptCard role="dialog" aria-label="MRČ Radio">
          <PromptClose
            type="button"
            aria-label="Zatvori"
            onClick={() => {
              setPromptDismissed(true);
              radio.declinePrompt();
            }}
          >
            <FiX size={15} />
          </PromptClose>
          <PromptTitle>MRČ Radio</PromptTitle>
          <PromptText>Hoćeš muziku domaće scene dok razgledaš?</PromptText>
          <PromptActions>
            <PromptYes
              type="button"
              onClick={() => {
                setPromptDismissed(true);
                radio.start();
              }}
            >
              <FiPlay size={14} /> Pusti
            </PromptYes>
            <PromptNo
              type="button"
              onClick={() => {
                setPromptDismissed(true);
                radio.declinePrompt();
              }}
            >
              Ne, hvala
            </PromptNo>
          </PromptActions>
        </PromptCard>
      )}

      <Stage>
        <Player $active={expanded} aria-hidden={!expanded}>
          <EqButton
            type="button"
            aria-label={radio.playing ? "Pauziraj radio" : "Pusti radio"}
            onClick={radio.togglePlay}
            $playing={radio.playing}
          >
            {radio.playing ? <FiPause size={16} /> : <FiPlay size={16} />}
          </EqButton>
          <TrackInfo>
            <TrackLabel>
              {radio.playing ? (
                <>
                  <Eq aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </Eq>
                  MRČ Radio
                </>
              ) : (
                "MRČ Radio, pauza"
              )}
            </TrackLabel>
            <TrackTitle title={radio.currentTrack?.title}>
              {radio.currentTrack?.title}
              {radio.currentTrack?.artist ? `, ${radio.currentTrack.artist}` : ""}
            </TrackTitle>
          </TrackInfo>
          <IconBtn
            type="button"
            aria-label="Sledeća pesma"
            onClick={radio.next}
          >
            <FiSkipForward size={16} />
          </IconBtn>
          <IconBtn
            type="button"
            aria-label="Skupi player"
            onClick={() => setExpanded(false)}
          >
            <FiX size={16} />
          </IconBtn>
        </Player>
        <Fab
          type="button"
          aria-label="Otvori MRČ Radio"
          onClick={() => setExpanded(true)}
          $playing={radio.playing}
          $active={!expanded}
          aria-hidden={expanded}
        >
          <FiMusic size={18} />
        </Fab>
      </Stage>
    </Wrap>
  );
};

export default RadioWidget;

/* ---------------------------------- style --------------------------------- */

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Fab i Player dele istu grid ćeliju (preklopljeni, dole-levo) pa se
   smenjuju čistom transform/opacity animacijom, bez skoka layouta. */
const Stage = styled.div`
  display: grid;
  align-items: end;
  justify-items: start;

  & > * {
    grid-area: 1 / 1;
  }
`;

const Wrap = styled.div`
  position: fixed;
  left: 1rem;
  bottom: calc(1rem + env(safe-area-inset-bottom));
  z-index: 1600;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;

  @media (max-width: 768px) {
    /* na product stranici mobilna CTA traka zauzima dno */
    bottom: calc(
      ${(p) => (p.$raised ? "5.5rem" : "1rem")} + env(safe-area-inset-bottom)
    );
  }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 106, 61, 0.35); }
  50% { box-shadow: 0 0 0 8px rgba(255, 106, 61, 0); }
`;

const Fab = styled.button`
  width: 46px;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--border-strong);
  background: var(--bg-surface);
  color: ${(p) => (p.$playing ? "var(--brand)" : "var(--text-primary)")};
  cursor: pointer;
  animation: ${(p) =>
    p.$playing && p.$active
      ? css`
          ${pulse} 2.2s ease-out infinite
        `
      : "none"};

  /* smooth smena sa playerom: raste iz ugla / nestaje u njega */
  transform-origin: left bottom;
  transform: ${(p) => (p.$active ? "scale(1)" : "scale(0.55)")};
  opacity: ${(p) => (p.$active ? 1 : 0)};
  visibility: ${(p) => (p.$active ? "visible" : "hidden")};
  pointer-events: ${(p) => (p.$active ? "auto" : "none")};
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.22s ease,
    visibility 0s linear ${(p) => (p.$active ? "0s" : "0.3s")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Player = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  max-width: min(320px, calc(100vw - 2rem));
  padding: 0.45rem 0.6rem;
  background: rgba(19, 16, 16, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);

  /* širi se iz fab dugmeta (donji levi ugao) i skuplja nazad u njega */
  transform-origin: left bottom;
  transform: ${(p) => (p.$active ? "scale(1)" : "scale(0.35)")};
  opacity: ${(p) => (p.$active ? 1 : 0)};
  visibility: ${(p) => (p.$active ? "visible" : "hidden")};
  pointer-events: ${(p) => (p.$active ? "auto" : "none")};
  transition:
    transform 0.32s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.24s ease,
    visibility 0s linear ${(p) => (p.$active ? "0s" : "0.32s")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const EqButton = styled.button`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: ${(p) => (p.$playing ? "var(--brand)" : "var(--bg-elevated)")};
  color: ${(p) => (p.$playing ? "#ffffff" : "var(--text-primary)")};
  cursor: pointer;
`;

const TrackInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
`;

const TrackLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--brand);
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const TrackTitle = styled.span`
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const eqBounce = keyframes`
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
`;

const Eq = styled.span`
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 10px;

  span {
    width: 2.5px;
    height: 100%;
    background: var(--brand);
    border-radius: 1px;
    transform-origin: bottom;
    animation: ${eqBounce} 0.9s ease-in-out infinite;
  }
  span:nth-child(2) {
    animation-delay: 0.25s;
  }
  span:nth-child(3) {
    animation-delay: 0.5s;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
    }
  }
`;

const PromptCard = styled.div`
  position: relative;
  width: min(280px, calc(100vw - 2rem));
  padding: 0.9rem 1rem;
  background: rgba(19, 16, 16, 0.96);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--border-strong);
  border-radius: 14px;
  animation: ${slideIn} 0.3s ease;
`;

const PromptClose = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const PromptTitle = styled.div`
  font-family: var(--font-display);
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--brand);
  margin-bottom: 0.25rem;
`;

const PromptText = styled.div`
  color: var(--text-primary);
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
`;

const PromptActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PromptYes = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.9rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-pill);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

const PromptNo = styled.button`
  padding: 0.45rem 0.9rem;
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;
